import React, { FC, MouseEvent, TouchEvent, useCallback } from "react";
import { useSelector } from "react-redux";
import { faEyeDropper, faPalette } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PaletteModal from "@/components/modal/components/palette";
import { useModal } from "@/hooks/use-modal";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { animationPaletteSelector } from "@/lib/state/selectors/animation-palette-selector";
import { editorSelector } from "@/lib/state/selectors/editor-selector";
import { useAppDispatch } from "@/lib/state/store";

import EditorButton from "../editor-button/editor-button";

import "./_palette.scss";

type PaletteProps = {
  visible?: boolean;
};

const Palette: FC<PaletteProps> = (props) => {
  const dispatch = useAppDispatch();
  const palette = useSelector(animationPaletteSelector);
  const editor = useSelector(editorSelector);
  const { openModal } = useModal();

  const onButtonPressed = useCallback(
    (e: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
      if (!(e.target instanceof HTMLButtonElement)) return;
      dispatch(
        editorActions.onColourChanged(
          parseInt(e.target.dataset.index ?? "0", 10),
        ),
      );
      e.preventDefault();
    },
    [dispatch],
  );

  const onEyeDropperSelected = useCallback(() => {
    dispatch(editorActions.onToolChanged("eyedropper"));
  }, [dispatch]);

  const onPaletteButtonPressed = useCallback(() => {
    openModal(() => <PaletteModal />);
  }, [openModal]);

  const buttons = palette.map((colour, index) => {
    const className =
      editor.currentColourIndex === index ? " palette-button--selected" : "";
    return (
      <li className="inline-block" key={index}>
        <button
          className={"palette-button" + className}
          style={{ background: colour }}
          data-index={index}
          onClick={onButtonPressed}
          onTouchEnd={onButtonPressed}
        />
      </li>
    );
  });

  const className = `palette ${props.visible ? "" : "palette--hidden"}`;

  return (
    <div>
      <ol className={className}>
        <li className="inline-block">
          <EditorButton
            onButtonPressed={onPaletteButtonPressed}
            label={<FontAwesomeIcon icon={faPalette} />}
          />
          <EditorButton
            onButtonPressed={onEyeDropperSelected}
            label={<FontAwesomeIcon icon={faEyeDropper} />}
            active={editor.currentTool === "eyedropper"}
          />
        </li>
        {buttons}
      </ol>
    </div>
  );
};

export default Palette;
